import {
  ApolloLink,
  Operation,
  FetchResult,
  Observable,
  NextLink,
} from '@apollo/client';
import { print } from 'graphql';
import { getMainDefinition } from '@apollo/client/utilities';
import { addToQueue } from './offlineQueue';
import { ownerForEnqueue, type QueueOwner } from './queueOwner';
import { isOnlineNow, isEffectivelyOnline } from './connectivity';
import { setBackendReachable } from './backendReachability';
import { isLegacyNetworkFailure, isDegradedNetworkFailure } from './transportFailure';
import { createOfflineQueuedError } from './isOfflineQueuedError';
import { isSessionChangedError } from '../gql/refreshFailure';

/**
 * Mutations que el backend acepta REPETIDAS —idempotencia por `clientOpId` +
 * `IdempotencyKey`, o transición convergente— según la auditoría del
 * 2026-09-08 (spec red-degradada § 0.9). Solo a estas se les pone techo de
 * espera y clasificación amplia: abortar una petición puede dejarla
 * procesándose en el servidor, y reproducirla después solo es seguro aquí.
 *
 * Fuera a propósito: `closeOrder` (403 sin código si ya estaba cerrada) y las
 * sub-mutations del asistente de actuación (crear una unidad dos veces la
 * duplica). Meter una operación nueva exige comprobar primero su replay en el
 * backend.
 */
export const TIMEOUT_QUEUEABLE_OPERATIONS: ReadonlySet<string> = new Set([
  'SaveSurveillanceRoute',
  'SaveGenericOrder',
  'SaveCampaign',
  'SaveCommunication',
  'CreateRouteSegment',
  'FinishRouteSegment',
  'DeleteRouteSegment',
  'UpdateSegmentPauseCause',
  'LinkOrderToSegment',
  'LinkExternalOrderToSegment',
  'UnlinkOrderFromSegment',
  // Corregir un tramo cerrado y editar sus campos: patch absoluto sobre un
  // tramo ya cerrado (auditado 2026-09-08) → reproducirlo converge.
  'CorrectRouteSegment',
  'UpdateRouteSegment',
  'StartSurveillanceRoute',
  'FinishSurveillanceRoute',
  'ValidateSurveillanceRoute',
  'AnnulSurveillanceRoute',
  'ReopenSurveillanceRoute',
  'UpdateOrder',
]);

/**
 * Saves cuyo `saveData` gestiona sessionStorage e imágenes y encola por su
 * cuenta en la cola COMPOUND: si el link los capturara habría doble encolado y
 * el replay duplicaría la entidad.
 *
 * Hasta 2026-09-08 estaban también `SaveGenericOrder`, `SaveCampaign` y
 * `SaveCommunication` (621b6abf, 2026-07-21), metidos porque el REPLAY de la
 * comunicación las ejecuta y no debía re-encolarlas. Eso dejó sin offline a
 * seis asistentes que dependían del link (orden genérica, campaña, duplicar,
 * editar campaña y los dos detalles). Ahora el replay —y cualquier llamador que
 * no quiera pasar por la cola— lo declara por contexto (`skipOfflineQueue`).
 */
export const COMPOUND_MANAGED_OPERATIONS: ReadonlySet<string> = new Set([
  'SaveAction',
  'SaveAccident',
]);

/**
 * Mutations con credenciales (contraseñas, tokens). NUNCA se encolan: la cola
 * vive en IndexedDB y sobrevive a cerrar la app; guardar ahí una contraseña o
 * un refresh token de 60 días es exactamente lo que `offlineSession.ts` evita
 * (spec § 12.3). Sin red, fallan con error, como debe ser.
 */
export const NEVER_QUEUE_OPERATIONS: ReadonlySet<string> = new Set([
  'SignIn',
  'SwitchTenant',
  'RegenerateAccessToken',
  'UpdatePassword',
  'ForgotPassword',
  'validateUser',
]);

/**
 * Mutations que solo tienen sentido con red: la gestión de grupos de las
 * banderitas (spec banderitas por grupo § 9.5). Las hace quien gestiona,
 * desde la oficina, y su resultado (quién comparte marcas con quién) se ve al
 * momento: reproducirlas horas después, con la lista de personas cambiada, es
 * peor que avisar «no se ha podido guardar». Sin red fallan al instante y la
 * pantalla enseña su error. NO se tiran de la cola al reproducir (a diferencia
 * de las de credenciales): nunca han entrado en ella.
 */
export const ONLINE_ONLY_OPERATIONS: ReadonlySet<string> = new Set([
  'CreateUserGroup',
  'RenameUserGroup',
  'DeleteUserGroup',
  'AddUserGroupMembers',
  'RemoveUserGroupMembers',
  // Configuración de la empresa («Revisión de tramos»): solo con red.
  'SetSegmentReviewAutoStates',
]);

/**
 * Techo de espera para las mutations de la allowlist, con los reintentos del
 * `RetryLink` DENTRO. Sin techo regía el del navegador (300 s en Chromium) por
 * cinco intentos. El backend corta su transacción a los 20 s; 25 s deja margen
 * a una respuesta lenta pero viva y sigue siendo soportable con guantes.
 */
export const OFFLINE_QUEUE_TIMEOUT_MS = 25_000;

export type QueuePolicy = 'timeout' | 'legacy' | 'skip';

/** Contexto Apollo que reconoce este link (`client.mutate({ context })`). */
export interface OfflineQueueContext {
  /** El llamador gestiona su propio offline (replay de la cola compound, padre de una comunicación). */
  skipOfflineQueue?: boolean;
  /** Techo distinto del default para una operación concreta. */
  offlineQueueTimeoutMs?: number;
}

export function resolveQueuePolicy(
  operationName: string,
  context: OfflineQueueContext,
): QueuePolicy {
  if (context?.skipOfflineQueue) return 'skip';
  if (NEVER_QUEUE_OPERATIONS.has(operationName)) return 'skip';
  if (ONLINE_ONLY_OPERATIONS.has(operationName)) return 'skip';
  if (COMPOUND_MANAGED_OPERATIONS.has(operationName)) return 'skip';
  if (TIMEOUT_QUEUEABLE_OPERATIONS.has(operationName)) return 'timeout';
  return 'legacy';
}

/**
 * Apollo Link que encola mutations cuando no hay red.
 *
 * - Sin red (fuente única de conectividad): encola y emite el centinela
 *   `offlineQueued` sin salir a la red.
 * - Con red: reenvía. Si la petición falla por red, encola y marca el error.
 *   Para las operaciones de `TIMEOUT_QUEUEABLE_OPERATIONS` hay además un techo
 *   de espera: al vencer, se desuscribe (aborta el `fetch` del `HttpLink` y
 *   cancela el `RetryLink`), encola y emite el centinela. Es la única forma de
 *   detectar la red DEGRADADA —la que `navigator.onLine` no ve— sin dejar al
 *   operario delante de un velo durante minutos.
 *
 * Fix 2026-07-21: se clasifica mutation-vs-query con `getMainDefinition`,
 * nunca con `definitions[0]` (los documentos con el fragment primero pasaban
 * por queries y no se encolaban jamás).
 */
export class OfflineQueueLink extends ApolloLink {
  request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    const definition = getMainDefinition(operation.query);
    const isMutation =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation';
    if (!isMutation) return forward(operation);

    const context = operation.getContext() as OfflineQueueContext;
    const policy = resolveQueuePolicy(operation.operationName, context);
    if (policy === 'skip') return forward(operation);

    // El dueño es el de la sesión que HACE la petición, no el de la que haya
    // cuando falle (spec § 12.2).
    const owner = ownerForEnqueue();

    // `isEffectivelyOnline`: si el sondeo ya dijo que el backend no contesta,
    // encolar AL INSTANTE en vez de gastar los 25 s del techo. El techo queda
    // para lo que de verdad lo necesita: servidor vivo y ESTA petición atascada.
    if (!isEffectivelyOnline()) {
      this.queueMutation(operation, owner).catch(console.error);
      return new Observable<FetchResult>((observer) => {
        observer.error(createOfflineQueuedError());
      });
    }

    return new Observable<FetchResult>((observer) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;

      const subscription = forward(operation).subscribe({
        next: (result) => {
          if (!settled) observer.next(result);
        },
        error: (error) => {
          if (settled) return;
          settled = true;
          if (timer) clearTimeout(timer);
          // La sesión cambió con la escritura en curso (spec § 12.11): ya no
          // puede salir con la de ahora, pero es trabajo de quien la empezó.
          // Se encola con SU dueño y sale cuando vuelva a entrar. Sin dueño
          // conocido no: `addToQueue` la sellaría con la sesión de ahora.
          const queueable =
            (isSessionChangedError(error) && owner !== undefined) ||
            (policy === 'timeout'
              ? isDegradedNetworkFailure(error)
              : isLegacyNetworkFailure(error));
          if (queueable) {
            this.queueMutation(operation, owner).catch(console.error);
            (error as any).offlineQueued = true;
          }
          observer.error(error);
        },
        complete: () => {
          if (settled) return;
          settled = true;
          if (timer) clearTimeout(timer);
          observer.complete();
        },
      });

      if (policy === 'timeout') {
        const timeoutMs =
          context?.offlineQueueTimeoutMs ?? OFFLINE_QUEUE_TIMEOUT_MS;
        timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          // Desuscribir aborta el fetch de HttpLink y cancela el RetryLink
          // (Apollo 3.14). Lo que el servidor pudiera estar procesando lo
          // resuelve el replay por idempotencia/convergencia.
          subscription.unsubscribe();
          console.warn(
            `[offline] ${operation.operationName} sin respuesta en ${timeoutMs} ms — encolada`
          );
          // Que el techo venza ES evidencia de que el backend no contesta: se
          // apunta, y lo siguiente que se guarde encola al instante en vez de
          // pagar otros 25 s. Cualquier respuesta que vuelva lo deshace.
          setBackendReachable(false);
          this.queueMutation(operation, owner).catch(console.error);
          observer.error(createOfflineQueuedError());
        }, timeoutMs);
      }

      return () => {
        if (timer) clearTimeout(timer);
        subscription.unsubscribe();
      };
    });
  }

  private async queueMutation(
    operation: Operation,
    owner?: QueueOwner
  ): Promise<void> {
    await addToQueue(
      {
        operationName: operation.operationName,
        query: print(operation.query),
        variables: operation.variables as Record<string, unknown>,
      },
      owner
    );
  }
}
